import { useAppContext, usePatientDetailContext } from 'context/hooks';
import dayjs from 'dayjs';
import { PlotScatterOptions, Point } from 'highcharts';
import { useWeightMeasurements } from 'hooks/api/useWeightData';
import { t } from 'i18next';
import { WeightIndicator } from 'models/weight';
import { formatDateHighcharts, getHighchartsDateTimeLabelFormats } from 'utils/dates';
import { displayWeight, getWeightUnits } from 'utils/helpers';
import { getWeightIndicatorColor } from 'utils/measurements/weight';

export const useWeightOptions = () => {
  const { startDate, endDate, unitSystem } = usePatientDetailContext();
  const { data, handleSelect } = useWeightMeasurements();
  const { currentUser } = useAppContext();

  return {
    title: '',
    chart: {
      type: 'scatter',
      height: 250,
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: getHighchartsDateTimeLabelFormats(currentUser?.userConfiguration.data.hourFormat),
      min: dayjs(startDate).unix() * 1000,
      max: dayjs(endDate).unix() * 1000,
    },
    yAxis: {
      title: { text: t('weight_label') },
      min: 0,
    },
    credits: { enabled: false },
    series: [
      {
        name: t('weight_label'),
        cursor: 'pointer',
        color: 'black',
        data: data?.map((e) => {
          return {
            x: new Date(e?.data.recordedTimestamp),
            y: displayWeight(e?.data.input?.weight, unitSystem),
            item: e,
          };
        }),
        marker: {
          symbol: 'circle',
        },
      },
    ],
    tooltip: {
      formatter(): string {
        const point = this as Point;
        if (typeof point.y === 'number') {
          return `${t('date_label')}: ${formatDateHighcharts(point.x, currentUser?.userConfiguration.data.hourFormat, true)} | ${t('weight_label')}: ${point.y}${getWeightUnits(unitSystem)}`;
        }
        return '';
      },
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        point: {
          events: {
            select(e: any) {
              // No other type possible
              handleSelect(e.target.item);
            },
          },
        },
      },
    },
    accessibility: {
      enabled: false,
    },
  } as PlotScatterOptions;
};

export const useBmiOptions = () => {
  const { startDate, endDate } = usePatientDetailContext();
  const { handleSelect, getMeasurementsByIndicator } = useWeightMeasurements();
  const { currentUser } = useAppContext();
  return {
    title: '',
    chart: {
      type: 'scatter',
      height: 250,
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: getHighchartsDateTimeLabelFormats(currentUser?.userConfiguration.data.hourFormat),
      min: dayjs(startDate).unix() * 1000,
      max: dayjs(endDate).unix() * 1000,
    },
    yAxis: {
      title: { text: t('bmi_label') },
      min: 0,
    },
    credits: { enabled: false },
    series: [
      {
        name: t('indicator_label_underweight'),
        cursor: 'pointer',
        color: getWeightIndicatorColor(WeightIndicator.Underweight),
        data: getMeasurementsByIndicator(WeightIndicator.Underweight)?.map((e) => {
          return {
            x: new Date(e.data.recordedTimestamp),
            y: e.data.value,
            item: e,
          };
        }),
        marker: {
          symbol: 'circle',
        },
      },
      {
        name: t('indicator_label_normal'),
        cursor: 'pointer',
        color: getWeightIndicatorColor(WeightIndicator.Normal),
        data: getMeasurementsByIndicator(WeightIndicator.Normal)?.map((e) => {
          return { x: new Date(e.data.recordedTimestamp), y: e.data.value, item: e };
        }),
        marker: {
          symbol: 'circle',
        },
      },
      {
        name: t('indicator_label_overweight'),
        cursor: 'pointer',
        color: getWeightIndicatorColor(WeightIndicator.Overweight),
        data: getMeasurementsByIndicator(WeightIndicator.Overweight)?.map((e) => {
          return { x: new Date(e.data.recordedTimestamp), y: e.data.value, item: e };
        }),
        marker: {
          symbol: 'circle',
        },
      },
      {
        name: t('indicator_label_obesity'),
        cursor: 'pointer',
        color: getWeightIndicatorColor(WeightIndicator.Obesity),
        data: getMeasurementsByIndicator(WeightIndicator.Obesity)?.map((e) => {
          return { x: new Date(e.data.recordedTimestamp), y: e.data.value, item: e };
        }),
        marker: {
          symbol: 'circle',
        },
      },
    ],
    tooltip: {
      formatter(): string {
        const point = this as Point;
        if (typeof point.y === 'number') {
          return `${t('date_label')}: ${formatDateHighcharts(point.x, currentUser?.userConfiguration.data.hourFormat, true)} | ${t('bmi_label')}: ${point.y?.toFixed(1)}`;
        }
        return '';
      },
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        point: {
          events: {
            select(e: any) {
              // No other type possible
              handleSelect(e.target.item);
            },
          },
        },
      },
    },
    accessibility: {
      enabled: false,
    },
  } as PlotScatterOptions;
};
